import React from 'react';
import { fmtNumber } from './kit';
import { tintClasses } from '../shared/tints';
import { FLAG, flagLevel, leadSegment } from './AttentionFinding';
import AcceptOwnerButton from './client/AcceptOwnerButton';
import { ownerStatsLine } from './client/ownerStats';

// Every piece of text in a row is normal weight (no bold anywhere); emphasis comes from
// colour and size, not weight.
//
// Type scale, set from the Now feed's rows: the finding (15px) and the client (14px) read
// as one pair, the stamp and owner label match the client (14px), and the chip sits a step
// below them (12px). Nothing here goes under 12px, and body text stays at 14px or more.
//
// One client in the list, in one fixed anatomy so every row scans the same way — the
// same look as the Now feed: a severity rail down the left edge, a chip, and ONE
// composed sentence about the client; the owner label sits on the right.
//
//   [chip] <Client> has 36 broken promises — last request 542 days ago     owner
//
// The sentence names the client as its subject, then its main signal, then how long
// since it last asked for something — never raw column values side by side.
//
// The WHOLE row is the control: one full-width button inside the <li> opens the client's
// full page and the entire row takes the hover / focus highlight, so the target is as
// big as what the eye reads. Nothing interactive nests inside it — the numbers
// in the sentence are plain text (the list's numbers are not links).

// The severity slot is never empty. `flagLevel` is null unless a client has broken
// promises or overdue work, so most Quiet / Steady rows and waiting-only rows have
// no flag — they get a calm neutral label in the same slot instead.
//
// A client in Needs Attention with no flag is there for exactly one reason — someone
// is waiting on them, or they need help — so the label and the why line both come
// from those two counts (waiting first), never from open work.
const CALM_LABELS = {
  attention: { label: 'Waiting', tint: 'waiting', rail: 'cyan' },
  // Help / needs-assistance is amber ("a person must act") everywhere else on the page —
  // the strip, the Work chips — so it is amber here too, not the cyan of "waiting on them".
  attentionHelp: { label: 'Needs help', tint: 'warn', rail: 'amber' },
  quiet: { label: 'Quiet', tint: 'neutral', rail: 'gray' },
  steady: { label: 'Steady', tint: 'neutral', rail: 'gray' },
};

// The rail down the row's left edge — the same three-level urgency read as the Now feed
// (red = leaking, amber = a person must act, gray = calm), plus cyan for "waiting on the
// client", which is a different party from "needs you".
const RAIL = {
  red: 'border-l-red-500',
  amber: 'border-l-amber-500',
  cyan: 'border-l-cyan-500',
  gray: 'border-l-gray-300 dark:border-l-gray-600',
};

// Flag level → rail colour. Critical and High risk are leaking (red); At risk is amber.
const FLAG_RAIL = { critical: 'red', high: 'red', watch: 'amber' };

/**
 * The severity badge for a row: its flag (Critical / High risk / At risk) when it
 * has one, else the calm label for its population. `rail` is the Tailwind class for the
 * row's left rail and `strong` says the headline gets the brighter text colour — the leaking rows. The weight is normal for every row.
 * @param {object} row
 * @returns {{label:string, className:string, kind:"flag"|"calm", rail:string, strong:boolean}}
 */
export const severityOf = (row) => {
  const level = flagLevel(row.brokenPromises || 0, row.overdue || 0);
  if (level) {
    return {
      label: FLAG[level].label,
      className: FLAG[level].cls,
      rule: FLAG[level].rule,
      kind: 'flag',
      rail: RAIL[FLAG_RAIL[level]],
      strong: level === 'critical' || level === 'high',
    };
  }
  const key = row.population === 'attention' && !(row.waitingOnThem > 0) ? 'attentionHelp' : row.population;
  const calm = CALM_LABELS[key] || CALM_LABELS.steady;
  const t = tintClasses(calm.tint);
  return { label: calm.label, className: `border ${t.surface} ${t.text}`, kind: 'calm', rail: RAIL[calm.rail], strong: false };
};

/**
 * The row's main counted signal as a { num, label } segment, or null when nothing is
 * counted. An unflagged client in Needs Attention leads with who is waiting (then help),
 * so open work never hides the reason; everyone else leads with broken promises, then
 * overdue, then waiting, open, help.
 * @param {object} row
 * @returns {{num:string,label:string}|null}
 */
const leadOf = (row) => {
  const flagged = flagLevel(row.brokenPromises || 0, row.overdue || 0) !== null;
  const counts = row.population === 'attention' && !flagged
    ? { waiting: row.waitingOnThem, help: row.needsHelp }
    : { broken: row.brokenPromises, overdue: row.overdue, waiting: row.waitingOnThem, open: row.openWork, help: row.needsHelp };
  return leadSegment(counts);
};

/**
 * How long since the client last asked for something, as the tail of the row's sentence.
 * Null days is "no request on record", distinct from 0 ("today").
 * @param {number|null|undefined} days
 * @returns {string}
 */
const recencyOf = (days) => {
  if (days == null) return 'no requests on record';
  if (days === 0) return 'last request today';
  return `last request ${fmtNumber(days)} ${days === 1 ? 'day' : 'days'} ago`;
};

/**
 * The row's one sentence, split so the client name can be its own element:
 * `${name} ${predicate} — ${tail}`. A quiet client's predicate is the silence itself;
 * everyone else's is their main counted signal; a client with nothing counted says so.
 * @param {object} row
 * @returns {{predicate:string, tail:string}}
 */
export const composeLine = (row) => {
  const tail = recencyOf(row.daysSinceAnyRequest);
  if (row.population === 'quiet') return { predicate: 'has gone quiet', tail };
  const lead = leadOf(row);
  if (!lead) return { predicate: 'has nothing that needs attention right now', tail };
  // "needs help" is a count of items, so it reads as a clause rather than "has 3 need help".
  if (/^needs? help$/.test(lead.label)) {
    return { predicate: `has ${lead.num} ${row.needsHelp === 1 ? 'item that needs' : 'items that need'} help`, tail };
  }
  return { predicate: `has ${lead.num} ${lead.label}`, tail };
};

// The row's resting and hover backgrounds. A new row is tinted blue (the "↑ N new" pill's
// colour) so it reads as different from the rows around it; the tint, not a badge, is the cue.
export const ROW_HOVER_CLASS = 'hover:bg-gray-100 dark:hover:bg-gray-800/60';
export const NEW_ROW_CLASS = 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30';

/** The amber "Unassigned" label's classes — shared with the list legend. */
export const UNASSIGNED_CLASS = 'text-sm font-normal text-amber-600 dark:text-amber-400';

/**
 * The severity chip: a mono-free uppercase tag in the row's severity colours. The list
 * legend renders this same component, so the legend and the rows cannot drift apart.
 * @param {object} props
 * @param {{label:string, className:string, kind:string, rule?:string}} props.severity  From severityOf().
 *   A flag's `rule` (where the level starts) is the chip's hover text.
 * @param {string} [props.testId]
 */
export function SeverityChip({ severity, testId }) {
  return (
    <span
      data-testid={testId}
      data-kind={severity.kind}
      title={severity.rule}
      className={`inline-flex items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-normal uppercase tracking-wider ${severity.className}`}
    >
      {severity.label}
    </span>
  );
}

/**
 * @param {object} props
 * @param {object} props.row          A list row (name, counts, population, …).
 * @param {boolean} [props.unassigned] The client is on v_clients_without_an_owner.
 * @param {boolean} [props.isNew]      The client just became flagged: the whole row is tinted
 *   (and announced as "New") until the reader opens it or the highlight expires.
 * @param {() => void} props.onOpen  Opens the client's full page.
 * @param {{member_id?:string, name?:string, messages_sent?:number, reply_rate?:number, tasks_delivered?:number, why?:string}|null} [props.suggestion]
 *   The proposed account owner for an unassigned client, with its evidence.
 * @param {boolean} [props.canAccept]  Offer Accept on the suggestion (canAssignAccountOwner()).
 * @param {() => void} [props.onOwnerAssigned]  Called after Accept's write succeeds.
 */
export default function ClientRow({
  row, unassigned, isNew = false, onOpen, suggestion = null, canAccept = false, onOwnerAssigned,
}) {
  const severity = severityOf(row);
  const line = composeLine(row);
  const proposed = unassigned && suggestion?.name ? suggestion : null;
  const stats = proposed ? ownerStatsLine(proposed, { withTasks: true }) : null;
  // Accept is a SIBLING of the row button, never inside it: a button cannot contain a button,
  // and a click on Accept must not also open the client.
  const showAccept = !!(proposed && canAccept && proposed.member_id);

  return (
    <li
      data-testid="client-row"
      data-new={isNew ? 'true' : undefined}
      className={`border-b border-gray-100 last:border-b-0 dark:border-gray-800${showAccept ? ' flex' : ''}`}
    >
      <button
        type="button"
        onClick={onOpen}
        data-testid="client-row-button"
        className={`flex w-full ${showAccept ? 'min-w-0 flex-1 ' : ''}${proposed ? 'flex-col items-start gap-1' : 'items-baseline justify-between gap-4'} border-l-4 ${severity.rail} px-4 py-3.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${isNew ? NEW_ROW_CLASS : ROW_HOVER_CLASS}`}
      >
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1.5">
            {isNew && <span className="sr-only">New. </span>}
            <SeverityChip severity={severity} testId="client-severity" />
            <span
              data-testid="client-why"
              className={`text-[15px] leading-snug font-normal ${severity.strong ? 'text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}
            >
              <span data-testid="client-name" className="capitalize">{row.name}</span>
              {` ${line.predicate}`}
              <span className="text-gray-500 dark:text-gray-400">{` — ${line.tail}`}</span>
            </span>
          </span>
        </span>

        {/* Right side: the owner label, and the proposed owner with its evidence once the
            backend can suggest one. Only "Unassigned" is knowable otherwise: no read returns an
            owner's name, so a client that is not on the unowned view shows nothing here
            rather than a guess. */}
        {unassigned && (
          <span className={proposed
            ? 'flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-left'
            : 'flex shrink-0 flex-col items-end gap-0.5 whitespace-nowrap text-right'}
          >
            <span data-testid="client-owner" className={UNASSIGNED_CLASS} aria-label="Unassigned">
              Unassigned
            </span>
            {proposed && (
              <span data-testid="client-owner-suggestion" className="whitespace-normal text-sm text-gray-700 dark:text-gray-300">
                {`Suggested: ${proposed.name}`}
                {stats && <span className="text-gray-500 dark:text-gray-400">{` — ${stats}`}</span>}
              </span>
            )}
          </span>
        )}
      </button>
      {showAccept && (
        <div data-testid="client-accept" className={`flex shrink-0 items-start px-4 py-2.5 ${isNew ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
          <AcceptOwnerButton clientId={row.id} memberId={proposed.member_id} onAssigned={onOwnerAssigned} />
        </div>
      )}
    </li>
  );
}
